// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useEffect, useCallback } from 'react';
import { listApprovals } from '../services/hitlService';
import type { HitlApproval } from '../types';

/** How often to poll HITL for new/updated approvals. */
const POLL_INTERVAL_MS = 10_000;

interface UseApprovalsParams {
  siteId?: string;
  approvalType?: string;
  /** Skip fetching entirely, e.g. when the consumer isn't currently shown. */
  enabled?: boolean;
}

interface UseApprovalsResult {
  approvals: HitlApproval[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApprovals({
  siteId,
  approvalType,
  enabled = true,
}: UseApprovalsParams): UseApprovalsResult {
  const [approvals, setApprovals] = useState<HitlApproval[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey(k => k + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // `isPoll` distinguishes the background 30s refresh from the initial/manual
    // load: a poll must never flash the loading spinner or blow away a
    // previously-successful list with an error banner over a transient blip.
    const load = async (isPoll: boolean) => {
      try {
        if (!isPoll) {
          setLoading(true);
          setError(null);
        }
        const result = await listApprovals({ siteId, approvalType });
        if (cancelled) return;
        setApprovals(
          [...result].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
        );
        if (isPoll) setError(null);
      } catch (err) {
        if (cancelled || isPoll) return;
        setError((err as Error).message ?? 'Failed to load approvals');
      } finally {
        if (!cancelled && !isPoll) setLoading(false);
      }
    };

    load(false);
    const intervalId = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [siteId, approvalType, enabled, fetchKey]);

  return { approvals, loading, error, refetch };
}
